import { useState } from 'react';
import { cx } from '../lib/cx';

export interface PhotoCompareProps {
  before: { url: string; caption: string };
  after: { url: string; caption: string };
  labels: { before: string; now: string };
  /** Tekst kad se slika ne učita. Bez njega ostaje samo neutralni okvir. */
  unavailableLabel?: string;
  className?: string;
}

/**
 * Usporedba prije/sada. Opis stoji ISPOD slike, izvan elementa s `overflow:hidden` —
 * točno onaj razred bugova koji je `.thumb-cap` proizveo (ARHITEKTURA.md sekcija 1).
 *
 * Potpisani URL vrijedi 5 minuta i pokazuje na drugu domenu (API), pa učitavanje
 * može pasti — istekao potpis, mreža, obrisan objekt. U tom slučaju ide neutralni
 * okvir umjesto slomljene ikone, a opis ostaje vidljiv (ARHITEKTURA.md sekcija 10).
 */
export function PhotoCompare({
  before,
  after,
  labels,
  unavailableLabel,
  className,
}: PhotoCompareProps) {
  const [failed, setFailed] = useState<Record<string, boolean>>({});

  return (
    <div className={cx('grid grid-cols-2 gap-4', className)}>
      {[
        { side: labels.before, ...before },
        { side: labels.now, ...after },
      ].map((p) => (
        <figure key={p.side} className="m-0">
          <div className="overflow-hidden rounded-lg bg-bg-alt">
            {failed[p.side] ? (
              <div
                role="img"
                aria-label={`${p.side} — ${unavailableLabel ?? p.caption}`}
                data-testid="photo-unavailable"
                className={cx(
                  'flex aspect-[3/4] w-full items-center justify-center',
                  'border border-dashed border-line px-3 text-center',
                )}
              >
                {unavailableLabel && (
                  <span className="text-[13px] text-ink-muted">{unavailableLabel}</span>
                )}
              </div>
            ) : (
              <img
                src={p.url}
                alt={`${p.side} — ${p.caption}`}
                loading="lazy"
                onError={() => setFailed((prev) => ({ ...prev, [p.side]: true }))}
                className="aspect-[3/4] w-full object-cover"
              />
            )}
          </div>
          <figcaption className="mt-2">
            <span className="eyebrow block">{p.side}</span>
            <span className="block text-[13px] text-ink-muted">{p.caption}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
